import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from "frontend-components";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DashboardProfile } from "../../utils/dashboard.types";

const PROFILE_NAME_MAX_LENGTH = 100;

interface DashboardProfileNameDialogProps {
    mode: "create" | "rename";
    open: boolean;
    profileName: string;
    isSubmitting: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (name: string) => Promise<DashboardProfile>;
}

export function DashboardProfileNameDialog({
    mode,
    open,
    profileName,
    isSubmitting,
    onOpenChange,
    onSubmit,
}: DashboardProfileNameDialogProps) {
    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!isSubmitting) onOpenChange(nextOpen);
            }}
        >
            {open && (
                <DashboardProfileNameDialogContent
                    key={`${mode}-${profileName}`}
                    mode={mode}
                    profileName={profileName}
                    isSubmitting={isSubmitting}
                    onOpenChange={onOpenChange}
                    onSubmit={onSubmit}
                />
            )}
        </Dialog>
    );
}

function DashboardProfileNameDialogContent({
    mode,
    profileName,
    isSubmitting,
    onOpenChange,
    onSubmit,
}: Omit<DashboardProfileNameDialogProps, "open">) {
    const { t } = useTranslation("hse");
    const inputId = useId();
    const errorId = useId();
    const [name, setName] = useState(profileName);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        const trimmedName = name.trim();

        if (!trimmedName) {
            setError(t("dashboard.profileNameRequired"));
            return;
        }

        if (trimmedName.length > PROFILE_NAME_MAX_LENGTH) {
            setError(t("dashboard.profileNameTooLong", { max: PROFILE_NAME_MAX_LENGTH }));
            return;
        }

        setError(null);

        try {
            await onSubmit(trimmedName);
            onOpenChange(false);
        } catch {
            setError(
                t(
                    mode === "create"
                        ? "dashboard.profileCreateError"
                        : "dashboard.profileRenameError",
                ),
            );
        }
    };

    return (
        <DialogContent
            className="bg-compendia-card text-compendia-foreground"
            showCloseButton={!isSubmitting}
        >
            <DialogHeader>
                <DialogTitle>
                    {t(mode === "create" ? "dashboard.createProfile" : "dashboard.renameProfile")}
                </DialogTitle>
                <DialogDescription>
                    {t(
                        mode === "create"
                            ? "dashboard.createProfileDescription"
                            : "dashboard.renameProfileDescription",
                    )}
                </DialogDescription>
            </DialogHeader>
            <form
                className="flex flex-col gap-2"
                onSubmit={(event) => {
                    event.preventDefault();
                    void handleSubmit();
                }}
            >
                <Label htmlFor={inputId}>{t("dashboard.profileName")}</Label>
                <Input
                    id={inputId}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={PROFILE_NAME_MAX_LENGTH}
                    disabled={isSubmitting}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    autoFocus
                />
                {error && (
                    <p id={errorId} role="alert" className="text-body-sm text-chameleon-pink">
                        {error}
                    </p>
                )}
                <DialogFooter className="mt-4">
                    <Button
                        type="button"
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={() => onOpenChange(false)}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button type="submit" variant="submit" size="submit" disabled={isSubmitting}>
                        {isSubmitting
                            ? t("common.saving")
                            : t(mode === "create" ? "common.create" : "common.save")}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
